import { gql } from "@apollo/client";
import { Box, Stack, Text } from "@chakra-ui/react";
import { Fragment } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate, useNavigate } from "react-router-dom";

import { PageTitle } from "../../../components/Page";
import { PageSpinner } from "../../../components/PageSpinner";
import { Panel } from "../../../components/Panel";
import { SupplierForm } from "../../../components/SupplierForm";
import { buildSupplierListPath, buildSupplierPagePath, RouteParams } from "../../../config/routes";
import {
  Currency,
  useSupplierEditPageGetSupplierQuery,
  useSupplierEditPageUpdateSupplierMutation,
} from "../../../generated/graphql";
import { useSingleParam } from "../../../hooks/useSingleParam";

export const SUPPLIER_EDIT_PAGE_GET_SUPPLIER = gql`
  query SupplierEditPageGetSupplier($supplierSlug: String!) {
    supplier(where: { slug: $supplierSlug }) {
      id
      name
      slug
      vat
      sageReference
      currency
    }
  }
`;

export const SUPPLIER_EDIT_PAGE_UPDATE_SUPPLIER = gql`
  mutation SupplierEditPageUpdateSupplier($input: UpdateSupplierInput!) {
    updateSupplier(input: $input) {
      id
      name
      slug
      vat
      sageReference
      currency
    }
  }
`;

export const SupplierEditPage = () => {
  const navigate = useNavigate();
  const supplierSlug = useSingleParam(RouteParams.SupplierSlug);

  const [updateSupplier] = useSupplierEditPageUpdateSupplierMutation();

  const { loading, error, data } = useSupplierEditPageGetSupplierQuery({
    variables: { supplierSlug },
  });

  if (loading) {
    return <PageSpinner />;
  }

  if (error) {
    console.error(error);
    return (
      <Stack spacing={4}>
        <Text>Something went wrong loading the supplier</Text>
      </Stack>
    );
  }

  if (!data || !data.supplier) {
    return <Navigate to={buildSupplierListPath()} />;
  }

  const { supplier } = data;

  return (
    <Fragment>
      <Helmet>
        <title>WMS | Supplier {supplier.name}</title>
      </Helmet>
      <Stack spacing={6} px={8} py={5}>
        <Stack width="100%" isInline justifyContent="space-between">
          <PageTitle>
            Edit Supplier{" "}
            <Text as="span" color="blue.500">
              {supplier.name}
            </Text>
          </PageTitle>
        </Stack>

        <Panel maxWidth="900px">
          <Box px={8} py={5}>
            <SupplierForm
              initialValues={{
                name: supplier.name,
                vat: supplier.vat,
                sageReference: supplier.sageReference ?? "",
                currency: supplier.currency ?? Currency.Gbp,
              }}
              onSubmit={async (values) => {
                const { data } = await updateSupplier({
                  variables: {
                    input: {
                      ...values,
                      supplierId: supplier.id,
                    },
                  },
                });

                if (data?.updateSupplier?.slug) {
                  navigate(buildSupplierPagePath(data.updateSupplier.slug));
                  return;
                }

                console.log("TODO: handle this");
              }}
            />
          </Box>
        </Panel>
      </Stack>
    </Fragment>
  );
};
